import { useCallback, useEffect, useState } from 'react';
import { Tag } from '@weave/schema-gen-ts/dist/schemas/tag/shared/v1/models.pb';
import { useQuery } from 'react-query';
import { TagsV2 } from '@frontend/api-tag';
import { VoiceMailboxApi } from '@frontend/api-voicemail-boxes';
import { VoicemailApi } from '@frontend/api-voicemails';
import { http } from '@frontend/fetch';
import { GlobalPanel } from '@frontend/panel-engine';
import { usePhoneConfigShallowStore } from '@frontend/phone-config';
import { useChannelSubscription } from '@frontend/queue';
import { useAppScopeStore, useScopedQuery } from '@frontend/scope';
import { createVoicemailObject } from '../components/voicemails/voicemail-utils';
import { getMailboxToNameMap } from '../components/voicemails/voicemails-multi.component';
import { queryKeys } from '../query-keys';
import { useVoicemailDetailsShallowStore } from '../stores/voicemail-details-store';
import { useVoicemailBoxList } from './use-voicemail-box';
import { useVoicemailMutations } from './use-voicemail-mutations';

export const useVoicemailChannelSubscription = () => {
  const { selectedLocationIds } = useAppScopeStore();
  const [shouldTriggerEffect, setShouldTriggerEffect] = useState(false);
  const {
    setVoicemailData,
    setCurrentRowId,
    setUserHasAccess,
    setVoicemailsQueryKey,
    toggleRowSelected,
    currentRowId,
    setDeleteVoicemail,
    setUpdateReadStatus,
    setIsDetached,
    setAutoplayRecording,
  } = useVoicemailDetailsShallowStore(
    'setVoicemailData',
    'setCurrentRowId',
    'setUserHasAccess',
    'setVoicemailsQueryKey',
    'toggleRowSelected',
    'currentRowId',
    'setDeleteVoicemail',
    'setUpdateReadStatus',
    'setIsDetached',
    'setAutoplayRecording'
  );

  const [voicemailId, setVoicemailId] = useState<string>();
  const { row, isFetching, isError, error } = useQueryVoicemailData(voicemailId, shouldTriggerEffect);

  const { updateReadStatus, deleteVoicemail } = useVoicemailMutations({
    selectedLocationIds,
    optimisticUpdateProps: {
      updateType: 'one',
      queryKey: ['voicemail', voicemailId ?? ''],
      onUpdateMutate: (data) => {
        setVoicemailData(data);
      },
      onUpdateError: (oldData) => {
        setVoicemailData(oldData);
      },
    },
  });

  /**
   * This effect is a very roundabout way of querying for the voicemail details for the 'query-then-open' workflow.
   * We rely on the voicemailId being set, to trigger the query in useQueryVoicemailData.
   * We then use that data to update the state and open the global panel.
   *
   * Ideally we would query it imperatively, but there are too many dependencies that cannot be changed easily in the queries required to create a voicemail object
   * that we use in the voicemail page.
   */
  useEffect(() => {
    if (!shouldTriggerEffect || isFetching) return;

    if (row) {
      setVoicemailData(row);
      GlobalPanel.open('voicemail-details');
      setShouldTriggerEffect(false);
    } else if (isError && http.isHttpError(error) && error.status === 403) {
      setUserHasAccess(false);
      GlobalPanel.open('voicemail-details');
      setShouldTriggerEffect(false);
    }
  }, [error, isFetching, isError, row, setUserHasAccess, setVoicemailData, shouldTriggerEffect]);

  useChannelSubscription(
    'voicemail-details',
    useCallback(
      (data) => {
        if (data.payload.type === 'open-detached') {
          setShouldTriggerEffect(false);
          setAutoplayRecording(false);
          setVoicemailData(data.payload.data);
          setDeleteVoicemail(deleteVoicemail);
          setUpdateReadStatus(updateReadStatus);
        } else if (data.payload.type === 'open-with-source') {
          setShouldTriggerEffect(false);
          setAutoplayRecording(false);
          setVoicemailData(data.payload.data);
          setCurrentRowId(data.payload.id);
          toggleRowSelected?.(data.payload.id, true, data.payload.index);
          GlobalPanel.open('voicemail-details');
        } else if (data.payload.type === 'query-then-open-detached') {
          // We set the query key here because this is the unique entry point for this workflow
          // (there is no better place to set it currently)
          setVoicemailsQueryKey(['voicemail', data.payload.voicemailId]);
          setUserHasAccess(true);
          setShouldTriggerEffect(true);
          setVoicemailId(data.payload.voicemailId);
          setAutoplayRecording(!!data.payload.autoplayRecording);
          setDeleteVoicemail(deleteVoicemail);
          setUpdateReadStatus(updateReadStatus);
        } else if (data.payload.type === 'close') {
          setShouldTriggerEffect(false);
          setAutoplayRecording(false);
          toggleRowSelected?.(currentRowId, false);
          setVoicemailData(undefined);
          setCurrentRowId('');
          setIsDetached(true);

          GlobalPanel.close('voicemail-details');
        }

        return true;
      },
      [
        currentRowId,
        deleteVoicemail,
        setCurrentRowId,
        setDeleteVoicemail,
        setVoicemailsQueryKey,
        setIsDetached,
        setAutoplayRecording,
        setUpdateReadStatus,
        setUserHasAccess,
        setVoicemailData,
        toggleRowSelected,
        updateReadStatus,
      ]
    )
  );
};

const useQueryVoicemailData = (messageId?: string, enabled?: boolean) => {
  const { selectedOrgId, selectedLocationIds } = useAppScopeStore();
  const { phoneConfig } = usePhoneConfigShallowStore('phoneConfig');

  const { data: voicemailBoxList } = useVoicemailBoxList({
    phoneConfig,
    locationIds: selectedLocationIds,
  });

  const { data: mappedTags = {} } = TagsV2.Queries.useListTagsQuery({
    request: { orgId: selectedOrgId, groupIds: selectedLocationIds },
    options: {
      enabled: !!messageId,
      select: (data) => {
        return data?.tags.reduce<Record<string, Tag>>((acc, tag) => {
          acc[tag.id] = tag;
          return acc;
        }, {});
      },
    },
  });
  const mailboxToNameMap = getMailboxToNameMap(voicemailBoxList ?? []);

  const { data, isFetching, isError, error } = useQuery(
    [selectedLocationIds, 'voicemail', messageId],
    async () => {
      if (messageId) {
        const _data = await VoicemailApi.getVoicemail({ messageId });

        /**
         * This creates the same data structure that we store in the voicemail table and list
         */
        return _data?.voicemail
          ? createVoicemailObject(_data.voicemail, selectedLocationIds, mappedTags, mailboxToNameMap)
          : undefined;
      }
      return Promise.resolve(undefined);
    },
    {
      enabled: enabled && !!messageId,
      staleTime: 0,
      cacheTime: 0,
    }
  );

  const { data: transcription, status: transcriptionStatus } = useScopedQuery({
    queryKey: queryKeys.voicemailTranscript(data?.voicemailId ?? ''),
    queryFn: async () => {
      const response = await VoiceMailboxApi.getTranscription(data?.voicemailId ?? '');
      return response;
    },
    enabled: !!data,
  });

  return {
    row: data,
    transcription,
    transcriptionStatus,
    isFetching,
    isError,
    error,
  };
};
